import { useRef } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@parrot-software/design-system';
import { ShieldCheck } from '@parrot-software/design-system/icons';
import { Trans, useTranslation } from 'react-i18next';

export type AccountVerificationDialogProps = {
  /**
   * The way out, and the switch between the two modes of the gate: present, the dialog can be
   * postponed; absent, the only button leads into the wizard.
   */
  onDismiss?: () => void;
  /** Ends the session. Not a dismissal: it is offered in both modes. */
  onSignOut: () => void;
  /** Takes the user into the wizard. The dialog does not know where that is. */
  onVerify: () => void;
};

/**
 * The dialog of the verification gate, drawn over the application that is already rendered.
 *
 * How many ways out it has is decided by whoever mounts it. `open` is fixed, so the dialog never
 * closes itself: it reports the intention and the App layer stops mounting it.
 *
 * Without `onDismiss` there is no cancel in the tree and `Escape` is prevented. The click outside is
 * not prevented here because it cannot be: an alert dialog already blocks it by construction.
 *
 * With `onDismiss`, `Escape` and the cancel button both arrive through the same handler, so there is
 * a single exit path. The verify button also closes natively, and the guard below keeps that close
 * from being read as a dismissal.
 *
 * It is raised above the legacy layout, which sits far above the z-index the design system gives an
 * alert dialog. The content is raised with a class; its overlay takes none, so a rule in the global
 * stylesheet raises it as the sibling right before this `data-testid`.
 *
 * The body names two things and the wizard asks for up to four. That is deliberate: the email and
 * the phone are the two the user recognises as theirs and the two that explain the benefit.
 *
 * @param {AccountVerificationDialogProps} props - Component properties
 */
export const AccountVerificationDialog = ({ onDismiss, onSignOut, onVerify }: AccountVerificationDialogProps) => {
  const { t } = useTranslation('identity');
  const isDismissible = Boolean(onDismiss);
  const verifyHandledRef = useRef(false);

  const handleVerify = () => {
    verifyHandledRef.current = true;
    onVerify();
  };

  /**
   * The single exit path: `Escape` and the cancel button both land here. The verify button closes
   * natively too, and that close is not a dismissal, so the guard swallows it.
   */
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    if (verifyHandledRef.current) {
      verifyHandledRef.current = false;

      return;
    }
    onDismiss?.();
  };

  return (
    <AlertDialog onOpenChange={handleOpenChange} open>
      <AlertDialogContent
        className="z-[1550] font-sans"
        data-testid="account-verification-dialog"
        onEscapeKeyDown={(event) => {
          if (!isDismissible) event.preventDefault();
        }}
      >
        <AlertDialogHeader>
          {/* The header centres its text below `sm`. This row is a flex box, so `text-center` no
              longer reaches the title and it has to follow both alignments itself. */}
          <div className="flex items-center justify-center gap-2 sm:justify-start">
            <ShieldCheck aria-hidden className="size-5 shrink-0 text-info" />
            <AlertDialogTitle className="text-xl font-semibold text-foreground">
              {t('verifyAccount.gate.title')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-sm text-muted-foreground">
            <Trans components={{ strong: <strong /> }} i18nKey="identity:verifyAccount.gate.body" />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button className="w-full sm:w-auto" onClick={onSignOut} variant="ghost">
            {t('verifyAccount.gate.signOut')}
          </Button>
          {isDismissible && <AlertDialogCancel>{t('verifyAccount.gate.dismiss')}</AlertDialogCancel>}
          <AlertDialogAction onClick={handleVerify}>{t('verifyAccount.gate.action')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
